import React, { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDesignSystem } from "../../lib/design/designSystem";
import clsx from "clsx";
import SearchIcon from "../icons/SearchIcon";
import CloseIcon from "../icons/CloseIcon";
import ChevronDownIcon from "../icons/ChevronDownIcon";

/**
 * GLASS SEARCHABLE SELECT COMPONENT
 *
 * Searchable select dropdown implementing the glass morphism design system
 * Based on SearchableSelect.tsx with Glass Design System styling
 */

interface Option {
  value: string;
  label: string;
}

interface GlassSearchableSelectProps {
  options: Option[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  multiSelect?: boolean;
}

const GlassSearchableSelect: React.FC<GlassSearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  disabled = false,
  error,
  helperText,
  className,
  multiSelect = false,
}) => {
  const { t } = useTranslation("common");
  const { surfaces, typography, components } = useDesignSystem();
  const resolvedPlaceholder = placeholder ?? t("pagination.select_placeholder");
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties>({});
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  );

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideWrapper = wrapperRef.current?.contains(target);
      const clickedInsideMenu = menuRef.current?.contains(target);
      if (!clickedInsideWrapper && !clickedInsideMenu) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Recalculate menu position when open / resize / scroll
  useEffect(() => {
    if (!isOpen) return;
    const calc = () => {
      const el = inputContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const desiredMaxHeight = 240;
      const height = Math.min(desiredMaxHeight, spaceBelow - 8);
      setMenuStyles({
        position: "absolute",
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
        maxHeight:
          height < 120 ? Math.min(desiredMaxHeight, rect.top - 8) : height,
        transform: height < 120 ? `translateY(-100%)` : "none",
        zIndex: 10000,
      });
    };
    calc();
    window.addEventListener("resize", calc);
    window.addEventListener("scroll", calc, true);
    return () => {
      window.removeEventListener("resize", calc);
      window.removeEventListener("scroll", calc, true);
    };
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lowercasedFilter = searchTerm
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    return options.filter((option) =>
      option.label
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .includes(lowercasedFilter)
    );
  }, [options, searchTerm]);

  const handleSelect = (option: Option) => {
    onChange(option.value);
    setSearchTerm("");
    if (!multiSelect) {
      setIsOpen(false);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm("");
    inputRef.current?.focus();
  };

  return (
    <div className={clsx("space-y-1", className)}>
      {label && <label className={typography.label}>{label}</label>}

      <div className="relative" ref={wrapperRef}>
        <div className="relative" ref={inputContainerRef}>
          <input
            ref={inputRef}
            type="text"
            value={isOpen ? searchTerm : selectedOption?.label || ""}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsOpen(true)}
            placeholder={selectedOption?.label || resolvedPlaceholder}
            disabled={disabled}
            className={clsx(
              surfaces.input,
              "pe-10",
              disabled && surfaces.inputDisabled,
              error && "border-rose-500/50 focus:border-rose-500/70"
            )}
          />
          <div className="absolute inset-y-0 end-0 pe-3 flex items-center gap-1">
            {value && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 text-white/60 hover:text-white transition-colors rounded"
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            )}
            <ChevronDownIcon
              className={clsx(
                "h-5 w-5 text-white/60 transition-transform",
                isOpen && "rotate-180"
              )}
            />
          </div>
        </div>

        {isOpen &&
          !disabled &&
          createPortal(
            <div
              ref={menuRef}
              style={menuStyles}
              className={clsx(
                surfaces.card,
                "py-1 text-base text-white focus:outline-none overflow-hidden"
              )}
            >
              <div className="max-h-60 overflow-auto">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <button
                      type="button"
                      key={option.value}
                      onClick={() => handleSelect(option)}
                      className={clsx(
                        components.dropdown.item,
                        value === option.value && components.dropdown.itemActive
                      )}
                    >
                      {option.label}
                    </button>
                  ))
                ) : (
                  <div className="px-2.5 py-3 text-center text-sm text-white/60">
                    {t("pagination.no_results_short")}
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>

      {error && <p className="text-xs text-rose-300">{error}</p>}
      {helperText && !error && <p className={typography.caption}>{helperText}</p>}
    </div>
  );
};

export default GlassSearchableSelect;
